import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Box, Button, Grid, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PreviewHeader from './PreviewHeader';
import AgentContactBar from './AgentContactBar';
import HighlightsGrid from './HighlightsGrid';
import FieldSection from './FieldSection';
import RemarksSection from './RemarksSection';
import PreviewFooter from './PreviewFooter';
import { usePreviewFields } from '../hooks';
import Carousel from './Carousel';
import PreviewContainer from './PreviewContainer';

const SECTION_MAPPING: Record<string, string> = {
  'Listing Agreement': 'Listing Agreement',
  'Transaction Participants': 'Transaction Participants',
};

const PreviewLayout: React.FC = () => {
  const fields = usePreviewFields();

  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  // Memoized so the array is only rebuilt when hook data actually changes
  const activeSections = useMemo(() => {
    const all = [
      {
        title: 'Remarks and Internet Settings',
        fields: fields.internetSettings,
        beforeFields: <RemarksSection showTitle />,
        columns: 2,
      },
      { title: 'Listing Agreement', fields: fields.listingDetails },
      { title: 'Transaction Participants', fields: fields.ownershipAgents },
      { title: 'HOA / Association', fields: fields.association },
      { title: 'Tax Information', fields: fields.taxInfo },
      { title: 'Property Details', fields: fields.propertyDetails },
      { title: 'Size & Rooms', fields: fields.sizeRooms },
      { title: 'Features & Amenities', fields: fields.features },
      { title: 'Showing Information', fields: fields.showingInfo },
    ];
    return all.filter((s) => s.fields?.length > 0);
  }, [fields]);

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  const handleToggle = useCallback(
    (title: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedSections((prev) => ({ ...prev, [title]: isExpanded }));
    },
    [],
  );

  const handleExpandAll = useCallback(() => {
    setExpandedSections(
      Object.fromEntries(activeSections.map((s) => [s.title, true])),
    );
  }, [activeSections]);

  const handleCollapseAll = useCallback(() => {
    setExpandedSections({});
  }, []);

  const handleScrollToSection = useCallback((buttonLabel: string) => {
    const sectionTitle = SECTION_MAPPING[buttonLabel];
    if (!sectionTitle) return;

    // Expand the section
    setExpandedSections((prev) => ({ ...prev, [sectionTitle]: true }));

    // Scroll to the section using the ref
    setTimeout(() => {
      const element = sectionRefs.current[sectionTitle];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  }, []);

  const isAllExpanded =
    activeSections.length > 0 &&
    activeSections.every((s) => !!expandedSections[s.title]);
  const isAllCollapsed =
    activeSections.length === 0 ||
    activeSections.every((s) => !expandedSections[s.title]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <PreviewHeader />

      <AgentContactBar />

      <PreviewContainer>
        <HighlightsGrid />

        <Carousel />

        <Grid
          container
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
          }}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                justifyContent: {
                  xs: 'center',
                  md: 'flex-start',
                },
                alignItems: 'center',
              }}
            >
              {Object.keys(SECTION_MAPPING).map((label) => (
                <Button
                  key={label}
                  onClick={() => handleScrollToSection(label)}
                  size="small"
                  sx={{ borderRadius: '20px', textTransform: 'none' }}
                  variant="outlined"
                >
                  {label}
                </Button>
              ))}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                justifyContent: {
                  xs: 'center',
                  md: 'flex-end',
                },
                alignItems: 'center',
              }}
            >
              <Button
                endIcon={<RemoveIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: 'text.primary',
                }}
                onClick={handleCollapseAll}
                disabled={isAllCollapsed}
              >
                Collapse All
              </Button>
              <Button
                endIcon={<AddIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: 'text.primary',
                }}
                onClick={handleExpandAll}
                disabled={isAllExpanded}
              >
                Expand All
              </Button>
            </Stack>
          </Grid>
        </Grid>

        <Box
          sx={{
            maxWidth: 1200,
            width: '100%',
            mx: 'auto',
            py: 4,
          }}
        >
          {activeSections.map((section) => (
            <FieldSection
              key={section.title}
              ref={(el) => {
                sectionRefs.current[section.title] = el;
              }}
              title={section.title}
              fields={section.fields}
              beforeFields={section.beforeFields}
              columns={section.columns}
              expanded={!!expandedSections[section.title]}
              onChange={handleToggle(section.title)}
              showGroups={true}
            />
          ))}
        </Box>
      </PreviewContainer>

      <PreviewFooter />
    </Box>
  );
};

export default PreviewLayout;
